import { z } from 'zod';

import { type Carrier, type SimSlot } from './carriers';
import { type RouterNetworkEventType, type RouterNetworkState } from './ws-messages';

export const ROUTER_NETWORK_HISTORY_DAYS = [7, 30] as const;

function isValidTimeZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    return true;
  } catch {
    return false;
  }
}

export const getRouterNetworkQuery = z.object({
  days: z.coerce
    .number()
    .refine((days) => ROUTER_NETWORK_HISTORY_DAYS.some((allowed) => allowed === days), {
      message: 'days must be 7 or 30',
    })
    .default(7),
  timezone: z.string().refine(isValidTimeZone, { message: 'Unknown time zone' }).default('UTC'),
});
export type GetRouterNetworkQuery = z.infer<typeof getRouterNetworkQuery>;

export const routerCapacityTestParams = z.object({
  id: z.string().min(1),
  testId: z.uuid(),
});
export type RouterCapacityTestParams = z.infer<typeof routerCapacityTestParams>;

export const switchRouterSimBody = z.object({ simId: z.uuid() });
export type SwitchRouterSimBody = z.infer<typeof switchRouterSimBody>;

export const readRouterDeviceBody = z.object({
  ipAddress: z.ipv4(),
  username: z.string().min(1),
  password: z.string().min(1),
});
export type ReadRouterDeviceBody = z.infer<typeof readRouterDeviceBody>;

export interface ReadRouterDeviceResponse {
  model: string;
  imei: string;
  activeSim: {
    slot: SimSlot | null;
    iccid: string;
    operator: string;
    apn: string;
    ipAddress: string;
    network: string;
  } | null;
}

export interface SwitchRouterSimResponse {
  state: RouterNetworkState;
}

export interface RouterNetworkSimSummary {
  id: string;
  slot: SimSlot;
  carrier: Carrier;
  isPrimary: boolean;
  isActive: boolean;
  lastActiveAt: string | null;
}

export interface RouterNetworkEventItem {
  id: string;
  createdAt: string;
  type: RouterNetworkEventType;
  fromCarrier: Carrier | null;
  toCarrier: Carrier | null;
  downtimeMs: number | null;
}

export interface RouterNetworkDay {
  day: string;
  minutesBySim: Record<string, number>;
  offlineMinutes: number;
  unknownMinutes: number;
  switches: number;
  streamDowntimeMinutes: number;
  uploadedBytes: number;
}

export interface RouterNetworkResponse {
  router: {
    id: string;
    name: string;
    patientId: string | null;
    patientName: string | null;
    networkState: RouterNetworkState | null;
    activeSimId: string | null;
    activeSimSince: string | null;
    lastSeenAt: string | null;
  };
  sims: RouterNetworkSimSummary[];
  events: RouterNetworkEventItem[];
  days: RouterNetworkDay[];
}

export interface RouterNetworkStatusItem {
  routerId: string;
  patientId: string;
  state: RouterNetworkState | null;
  carrier: Carrier | null;
  onPrimary: boolean;
  testing: boolean;
  changedAt: string | null;
}

export type RouterCapacityTestStatus = 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';
export type CapacityTestPhase = 'profile' | 'ramp';
export type CapacityTestCameraKind = 'axis' | 'reolink';

export interface RouterCapacityTestResultItem {
  id: string;
  phase: CapacityTestPhase;
  cameraKind: CapacityTestCameraKind;
  profile: string;
  concurrentStreams: number;
  expectedFps: number | null;
  receivedFps: number | null;
  deliveryPercent: number | null;
  throughputKbps: number;
  startupMs: number | null;
  maxFrameGapMs: number | null;
  failedStreams: number;
  passed: boolean;
}

export interface RouterCapacityTestItem {
  id: string;
  createdAt: string;
  status: RouterCapacityTestStatus;
  carrier: Carrier | null;
  startedAt: string | null;
  finishedAt: string | null;
  failureReason: string | null;
  recommendedLowProfile: string | null;
  recommendedHighProfile: string | null;
  sustainableHighStreams: number | null;
  sustainableLowStreams: number | null;
  results: RouterCapacityTestResultItem[];
}

export interface RouterCapacityTestPreflight {
  axisCameras: number;
  reolinkCameras: number;
  liveViewers: number;
  inMonitoringHours: boolean;
  estimatedMinutes: number;
  estimatedMegabytes: number;
  blockedReason: string | null;
}
